import { Box, Theme, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  PayoutStageProgressStatus,
  PayoutWorklistStageProgress,
} from './types';

interface StageProgressProps {
  stage?: PayoutWorklistStageProgress;
}

const STAGE_KEYS: Array<keyof PayoutWorklistStageProgress> = ['onboarding', 'funding', 'distribution'];
const STAGE_LABELS: Record<keyof PayoutWorklistStageProgress, string> = {
  onboarding: 'Onboarding',
  funding: 'Funding',
  distribution: 'Distribution',
};

const useStyles = makeStyles()((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    minWidth: '150px',
  },
  segments: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '6px',
  },
  labelsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '6px',
  },
  labelCell: {
    display: 'flex',
    justifyContent: 'center',
  },
  segment: {
    height: '8px',
    borderRadius: '999px',
    backgroundColor: theme.palette.secondary.dark,
  },
  inProgressSegment: {
    backgroundColor: '#FFD900',
  },
  activeSegment: {
    boxShadow: `0 0 0 1px ${theme.palette.info.main}`,
  },
  completedSegment: {
    backgroundColor: '#88B856',
  },
  label: {
    color: theme.palette.text.secondary,
    fontFamily: 'Inter',
    fontSize: '12px',
    whiteSpace: 'nowrap',
  },
}));

const StageProgress = ({ stage }: StageProgressProps) => {
  const { classes, cx } = useStyles();

  const safeStage: PayoutWorklistStageProgress = stage || {
    onboarding: PayoutStageProgressStatus.NotStarted,
    funding: PayoutStageProgressStatus.NotStarted,
    distribution: PayoutStageProgressStatus.NotStarted,
  };

  const inProgressStage = STAGE_KEYS.find(
    (key: keyof PayoutWorklistStageProgress) =>
      safeStage[key] === PayoutStageProgressStatus.InProgress,
  );

  let lastCompletedStage: keyof PayoutWorklistStageProgress | undefined;
  for (let index = STAGE_KEYS.length - 1; index >= 0; index -= 1) {
    const key = STAGE_KEYS[index];
    if (safeStage[key] === PayoutStageProgressStatus.Completed) {
      lastCompletedStage = key;
      break;
    }
  }

  const currentStageKey: keyof PayoutWorklistStageProgress =
    inProgressStage || lastCompletedStage || 'onboarding';

  return (
    <Box className={classes.container} aria-label={`Current stage ${STAGE_LABELS[currentStageKey]}`}>
      <Box className={classes.segments}>
        {STAGE_KEYS.map((key) => (
          <Box
            key={key}
            className={cx(
              classes.segment,
              safeStage[key] === PayoutStageProgressStatus.InProgress && classes.inProgressSegment,
              safeStage[key] === PayoutStageProgressStatus.Completed && classes.completedSegment,
              key === currentStageKey && classes.activeSegment,
            )}
          />
        ))}
      </Box>
      <Box className={classes.labelsRow}>
        {STAGE_KEYS.map((key) => (
          <Box key={`${key}-label`} className={classes.labelCell}>
            {key === currentStageKey && (
              <Typography className={classes.label}>{STAGE_LABELS[key]}</Typography>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default StageProgress;
